import { useCallback, useMemo } from 'react';
import { useIsFocused } from '@react-navigation/native';
import { type AddressType, useAppState, useUrqlPolling } from '@sg/garnish';
import {
  type CourierDetails,
  FlattenedOrderStatuses,
} from '@sg/graphql-schema';

import { useOrderStatusPollingQuery } from '../../graphql/OrderStatus.generated';

/**
 * Polling for the Order Status Screen.
 * Will have different polling intervals depending on order status.
 */
export function useOrderStatusPolling(
  orderId: string,
  orderType: AddressType,
  orderStatus: FlattenedOrderStatuses,
  courierDetails?: CourierDetails,
) {
  //
  // ─── Flags ───────────────────────────────────────────────────────────

  const isScreenFocused = useIsFocused();

  const isCompletedOrder = orderStatus === FlattenedOrderStatuses.Completed;
  const isFailedOrder = orderStatus === FlattenedOrderStatuses.Failed;
  const isFinalOrderStatus = isCompletedOrder || isFailedOrder;

  const shouldSkipPolling = !isScreenFocused || isFinalOrderStatus;

  // ─── Remote Data ─────────────────────────────────────────────────────

  const input = useMemo(() => ({ variables: { id: orderId } }), [orderId]);

  const [, refetchOrderStatus] = useOrderStatusPollingQuery({
    variables: input.variables,
    requestPolicy: 'cache-and-network',
    pause: true,
  });

  // ─────────────────────────────────────────────────────────────────────

  const refetchOrderStatusOnAppVisible = useCallback(() => {
    if (shouldSkipPolling) return;

    refetchOrderStatus();
  }, [refetchOrderStatus, shouldSkipPolling]);

  // ─── App State ───────────────────────────────────────────────────────

  const appState = useAppState({ onVisible: refetchOrderStatusOnAppVisible });
  const isAppActive = appState === 'active';

  // ─────────────────────────────────────────────────────────────────────

  const timing = getOrderStatusTiming(orderType, orderStatus);

  const { response } = useUrqlPolling(
    timing,
    input,
    useOrderStatusPollingQuery,
    shouldSkipPolling || !isAppActive,
  );

  const polledStatus = response?.data?.orderStatus?.flattenedOrderStatus;
  const status = polledStatus ?? orderStatus;
  const courier = response?.data?.orderStatus?.courierDetails ?? courierDetails;

  // ─────────────────────────────────────────────────────────────────────

  return { status, courier, refetchOrderStatus };
}

// ─── Helpers ───────────────────────────────────────────────────────────

function getOrderStatusTiming(
  orderType: AddressType,
  orderStatus: FlattenedOrderStatuses,
) {
  if (orderStatus === FlattenedOrderStatuses.Delivering) {
    return IN_DELIVERY_POLLING_INTERVAL;
  }

  if (orderType === 'delivery') {
    return PRE_DELIVERY_POLLING_INTERVAL;
  }

  return DEFAULT_POLLING_INTERVAL;
}

// ─── Constants ─────────────────────────────────────────────────────────

const DEFAULT_POLLING_INTERVAL = 1000 * 60; // 60 seconds.
const IN_DELIVERY_POLLING_INTERVAL = 1000 * 10; // 10 seconds.
const PRE_DELIVERY_POLLING_INTERVAL = 1000 * 30; // 30 seconds.
